/**
 * Slim sticky header on scroll (13/10 homepage polish 2, business/Shopify#50 item 6f). Site-wide, desktop >= 1025 px.
 * After ~100 px of scroll the sticky header drops its bottom row (menu), so logo + search + icons stay as one row.
 * No layout shift: the header section keeps its full height reserved (min-height, measured before the switch);
 * the reserved area under the slim row is see-through and click-through (Ella sets pointer-events: none on the
 * sticky section and auto on its rows). The top row keeps its geometry, so nothing in it moves.
 * Only when Ella's sticky header is active (Ella makes it sticky only with prefers-reduced-motion: no-preference).
 * Hysteresis (on > 100 px, off < 40 px) avoids flicker. Phones: the header is one row already; the announcement
 * bar is a separate, non-sticky section, so it scrolls away on its own.
 *
 * Phones and tablets (< 1025 px; owner 14/10, business/Shopify#50): the header hides while scrolling down and comes
 * back on any scroll up. It slides with transform only (root class spo-header-hide; CSS in gtx-custom.css), so
 * nothing below it moves (no layout shift); the collection chip bar slides with it. It stays visible near the top
 * of the page, while focus is inside it (keyboard), while a drawer or menu is open, and with
 * prefers-reduced-motion: reduce (then it simply stays put). A 8 px dead band ignores scroll jitter.
 */
(() => {
  // Looked up on every update: the header element is replaced after this deferred script first runs, and a
  // detached element reports no position (that left slim mode off).
  const getSection = () => document.getElementById('header-component')?.closest('.header-group-section');
  let section = null;

  const desktop = window.matchMedia('(min-width: 1025px)');
  const root = document.documentElement;
  let slim = false;
  let ticking = false;

  const set = (on) => {
    if (on === slim) return;
    if (on) {
      section.style.minHeight = `${section.offsetHeight}px`;
      root.classList.add('spo-header-slim');
    } else {
      root.classList.remove('spo-header-slim');
      if (section) section.style.minHeight = '';
    }
    slim = on;
  };

  const update = () => {
    ticking = false;
    const current = getSection();
    if (current !== section) {
      if (section) section.style.minHeight = '';
      slim = false;
      root.classList.remove('spo-header-slim');
      section = current;
    }
    if (!section || !desktop.matches || getComputedStyle(section).position !== 'sticky') return set(false);
    const y = window.scrollY;
    if (!slim && y > 100) set(true);
    else if (slim && y < 40) set(false);
  };

  /* ---- Phones / tablets: hide on scroll down, show on scroll up ---- */
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const JITTER = 8;
  let lastY = window.scrollY;
  let hidden = false;

  const hide = (on) => {
    if (on === hidden) return;
    hidden = on;
    root.classList.toggle('spo-header-hide', on);
  };

  /** Something in or from the header is open or focused: keep it on screen. */
  const busy = (header) =>
    header.contains(document.activeElement) ||
    root.classList.contains('spo-ymm-open') ||
    document.querySelector('side-drawer.active, side-drawer.open') !== null ||
    /(^|\s)overflow-hidden/.test(document.body.className);

  const updateMobile = () => {
    const y = Math.max(0, window.scrollY);
    const delta = y - lastY;
    if (Math.abs(delta) < JITTER) return;
    lastY = y;
    const header = section || getSection();
    if (!header || desktop.matches || reduce.matches || getComputedStyle(header).position !== 'sticky') return hide(false);
    if (y <= header.offsetHeight || delta < 0 || busy(header)) return hide(false);
    hide(true);
  };

  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        update();
        updateMobile();
      });
    },
    { passive: true }
  );
  // Keyboard focus moving into a hidden header brings it back.
  document.addEventListener('focusin', (event) => {
    if (hidden && section && section.contains(event.target)) hide(false);
  });
  desktop.addEventListener('change', () => {
    hide(false);
    update();
  });
  reduce.addEventListener('change', () => hide(false));
  update();
})();
